import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScaleLoader } from 'react-spinners';
import type {
  CalendarProduct,
  CalendarProps,
  KlaviyoZapierPayload,
  MedispaCalendarConfigObject,
  SesamiReservationObject,
  SesamiSDKGlobal,
  SesamiSlotObject,
} from '@/@types/calendar';
import { AddToCartPayload, Product } from '@/@types/shopify';
import { formatMoney } from '@/entrypoints/utils';
import CalendarConfirm from './CalendarConfirm';
import CalendarDayTitles from './CalendarDayTitles';
import CalendarGrid from './CalendarGrid';
import CalendarHead from './CalendarHead';
import CalendarTimeSlots from './CalendarTimeSlots';
import ClinicDetails from './ClinicDetails';
import ClinicTab from './ClinicTab';
import {
  calendarConfigAtom,
  calendarLoadingAtom,
  dayAvailabilitiesAtom,
  errorMessageAtom,
  productSesamisAtom,
  selectedDayAtom,
  selectedProductAtom,
  selectedTimeSlotAtom,
} from './calendar-atoms';
import ErrorPopup from './ErrorPopup';

function getCleanClinicName({
  clinics,
  handle,
  defaultName,
  template,
}: {
  clinics: CalendarProps['clinics'];
  handle: string;
  defaultName: string;
  template: string;
}) {
  let matchingClinicHandle = Object.keys(clinics).find((key) => handle.indexOf(key) !== -1);

  if (!matchingClinicHandle && handle.includes('-discovery-bar')) {
    matchingClinicHandle = Object.keys(clinics).find((key) => key.includes('central'));
  }

  if (!matchingClinicHandle) {
    return defaultName;
  }

  const matchingClinic = clinics[matchingClinicHandle];
  return template.replace('{{location_name}}', matchingClinic.display_name);
}

function cleanProductName(productName: string) {
  // Remove any location suffixes
  productName = productName.split(' - ')[0];

  // Remove "Bespoke Luxury" from the title
  return productName.replace(/bespoke luxury/gi, '').trim();
}

async function getDownpaySellingPlan(handle: string): Promise<number> {
  try {
    const liquidDataResponse = await fetch(
      window.Shopify.routes.root + `products/${handle}?sections=product-calendar-config`,
    );

    if (!liquidDataResponse.ok) {
      console.error('Failed to fetch product selling plans');

      const errorMessage = await liquidDataResponse.text();
      throw new Error(errorMessage);
    }

    const data = await liquidDataResponse.json(),
      configHtml = data['product-calendar-config'];

    if (!configHtml) {
      throw new Error('Product selling plans response was blank?');
    }

    const fragment = document.createDocumentFragment(),
      placeholder = document.createElement('div');

    placeholder.innerHTML = configHtml;

    fragment.appendChild(placeholder);

    const sellingGroupsRaw = fragment.querySelector("script[id^='calendar-config--']")?.textContent;
    const sellingGroupsJson = JSON.parse(sellingGroupsRaw ?? '{}');

    if (typeof sellingGroupsJson['deposit_selling_plan'] === 'undefined') {
      console.error('Could not find deposit_selling_plan');
      return 0;
    }

    return sellingGroupsJson['deposit_selling_plan']?.id ?? 0;
  } catch (e) {
    throw new Error(e instanceof Error ? e.message : (e as string).toString());
  }
}

export default function CalendarRoot({ config }: { config: MedispaCalendarConfigObject }) {
  const [selectedProduct, setselectedProduct] = useAtom(selectedProductAtom);

  const onExternalConfigUpdate = useCallback(() => {
    console.debug('External config update', window.theme.medispaCalendarConfig);
    setselectedProduct(window.theme.medispaCalendarConfig.selectedProduct);
  }, [setselectedProduct]);

  const loading = useAtomValue(calendarLoadingAtom);

  const productName = useMemo(() => {
    const product = config.products.find((product) => product.id.toString() === selectedProduct);
    return cleanProductName(product?.name || '');
  }, [selectedProduct, config.products]);

  // Per-product deposit amount. Older cached products lack the field,
  // so fall back to the config value.
  const depositFullAmount = useMemo(() => {
    const product = config.products.find((product) => product.id.toString() === selectedProduct);
    return product?.compare_at_price ?? config.depositAmount;
  }, [selectedProduct, config.products, config.depositAmount]);

  const inStoreFullAmount = useMemo(() => {
    const product = config.products.find((product) => product.id.toString() === selectedProduct);
    return product?.pay_in_store_amount ?? config.payInStoreAmount;
  }, [selectedProduct, config.products, config.payInStoreAmount]);

  const setConfig = useSetAtom(calendarConfigAtom);
  const [productSesamis, setProductSesamis] = useAtom(productSesamisAtom);

  const [selectedDay, setSelectedDay] = useAtom(selectedDayAtom);

  const [selectedDayAvailabilities, setSelectedDayAvailabilities] = useState<
    SesamiSlotObject['slots']
  >([]);

  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);

  const dayAvailabilities = useAtomValue(dayAvailabilitiesAtom);

  const [selectedTimeSlot, setSelectedTimeSlot] = useAtom(selectedTimeSlotAtom);

  const reserveSlot = async () => {
    if (!selectedTimeSlot) {
      return;
    }

    try {
      console.debug('Reserving slot', selectedTimeSlot);

      const sesami = productSesamis.get(selectedProduct);
      if (!sesami) {
        return;
      }

      const data: SesamiReservationObject = await sesami.reserve(selectedTimeSlot);

      console.debug('Reserved slot', data);

      const productBeingAdded = config.products.find(
        (product) => product.id.toString() === selectedProduct,
      );

      if (!productBeingAdded) {
        console.warn('Could not find product being added');
        return;
      }

      const remainingAmountLineItemValue = config.labels.remaining_amount_pending_calculation;

      // Get the correct Downpay selling ID
      let sellingPlan = 0;

      const productDataFromConfig = config.products.find(
        (product) => product.id.toString() === selectedProduct,
      );

      if (productDataFromConfig) {
        sellingPlan = await getDownpaySellingPlan(productDataFromConfig.handle);
      }

      // Add product to cart
      const body: AddToCartPayload = {
        items: [
          {
            id: productBeingAdded.variant_id,
            quantity: 1,
            properties: {
              _source: 'medispa-calendar:handleReserveSlot',
              Date: selectedTimeSlot.startTime.split(' ')[0],
              Time: selectedTimeSlot.startTime.split(' ')[1],
              Timezone: config.timezone,
              'Team Member': '',
              _reservation_token: data.token,
              _locale: 'en',
              '_remaining-slots': selectedTimeSlot.remainingSlots - 1,
              [config.labels.remaining_amount_line_item_label]: remainingAmountLineItemValue,
            },
          },
        ],
      };

      if (sellingPlan) {
        body.items[0]['selling_plan'] = sellingPlan.toString();
      }

      console.debug('Adding product to cart', body);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response?.ok) {
        console.error('Failed to add product to cart', response);
        setErrorMessage(config.labels.error_popup_message);
        return;
      }

      console.debug('Product added to cart');

      handleClose();
    } catch (error) {
      console.error('Failed to reserve slot', error);
    }
  };

  useEffect(() => {
    setConfig(config);
  }, [config]);

  useEffect(() => {
    document.addEventListener('theme:medispaCalendarConfig', onExternalConfigUpdate);

    return () => {
      document.removeEventListener('theme:medispaCalendarConfig', onExternalConfigUpdate);
    };
  }, []);

  const appointmentPrefillRef = useRef<{
    date: string;
    time: string;
    product: CalendarProduct | null;
  } | null>(null);

  useEffect(() => {
    const getClinicDetails = (handle: string) => {
      let clinicHandle = Object.keys(config.clinics).find((key) => handle.indexOf(key) !== -1);

      if (!clinicHandle && handle.includes('-discovery-bar')) {
        clinicHandle = Object.keys(config.clinics).find((key) => key.includes('central'));
      }

      if (!clinicHandle) {
        return '';
      }

      return config.clinics[clinicHandle].display_name;
    };

    const handleKlaviyoEvent = (event: Event) => {
      console.debug('Handling Klaviyo event', event);
      const { type } = (event as CustomEvent).detail;

      if (type === 'open') {
        // Wait for Klaviyo form DOM to render, then prefill and lock date/time fields
        const prefill = appointmentPrefillRef.current;
        const appointmentDate = prefill?.date ?? '';
        const appointmentTime = prefill?.time ?? '';
        const appointmentClinic = getClinicDetails(prefill?.product?.handle ?? '');

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )!.set!;

        const setReactInputValue = (input: HTMLInputElement, value: string) => {
          nativeInputValueSetter.call(input, value);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        const observer = new MutationObserver(() => {
          const dateInputs = document.querySelectorAll<HTMLInputElement>(
            'input[id^="Appointment_Date_"]',
          );
          const timeInputs = document.querySelectorAll<HTMLInputElement>(
            'input[id^="Appointment_Time_"]',
          );
          const clinicInputs = document.querySelectorAll<HTMLInputElement>(
            'input[id*="downshift-"]',
          );
          
          clinicInputs.forEach((input) => {
            input.addEventListener('click', () => {
              console.debug('Clinic input clicked', input);
              if (input.value !== '') {
                setReactInputValue(input, '');
              }
            });
          });

          if (dateInputs.length > 0 || timeInputs.length > 0) {
            setTimeout(() => {
              dateInputs.forEach((input) => {
                setReactInputValue(input, appointmentDate);
              });
              timeInputs.forEach((input) => {
                setReactInputValue(input, appointmentTime);
              });

              observer.disconnect();
            }, 1000);
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      }

      if (type === 'submit') {
        const webhookUrl = config.zapierWebhookUrl;
        if (webhookUrl) {
          const detail = (event as CustomEvent).detail ?? {};
          const metaData = (detail.metaData ?? {}) as Record<string, string>;
          const product = appointmentPrefillRef.current?.product;

          // Build the payload key-by-key so Zapier always sees the same
          // schema, whatever fields Klaviyo omits.
          const payload: KlaviyoZapierPayload = {
            type: detail.type ?? '',
            formId: detail.formId ?? '',
            formVersionId: detail.formVersionId ?? 0,
            companyId: detail.companyId ?? '',
            metaData: {
              $source: metaData['$source'] ?? '',
              $first_name: metaData['$first_name'] ?? '',
              $last_name: metaData['$last_name'] ?? '',
              $email: metaData['$email'] ?? '',
              $phone_number: metaData['$phone_number'] ?? '',
              'Medispa Clinic': metaData['Medispa Clinic'] ?? '',
              'Appointment Date': metaData['Appointment Date'] ?? '',
              'Appointment Time': metaData['Appointment Time'] ?? '',
              coupon: metaData['coupon'] ?? '',
            },
            product: product
              ? {
                  id: product.id,
                  variant_id: product.variant_id,
                  handle: product.handle,
                  title: product.name,
                  featured_image: product.featured_image,
                }
              : null,
            locale: window.Shopify.locale,
          };

          fetch(webhookUrl, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).catch((err) => {
            console.error('Failed to send Klaviyo form data to Zapier', err);
          });
        }
      }
    };

    window.addEventListener('klaviyoForms', handleKlaviyoEvent);

    return () => {
      window.removeEventListener('klaviyoForms', handleKlaviyoEvent);
    };
  }, [config.zapierWebhookUrl]);

  useEffect(() => {
    // Initialise any products missing from the Sesami SDK cache

    const updatedProductSesamis = new Map(productSesamis);

    const missingProducts = config.products.filter(
      (product) => !productSesamis.has(product.id.toString()),
    );

    if (missingProducts.length > 0) {
      missingProducts.forEach((product) => {
        const sesami = new window.SesamiSDK({
          shopId: config.shopId,
          productId: product.id.toString(),
          variantId: product.variant_id.toString(),
          quantity: 1,
          autoLoad: true,
        });
        updatedProductSesamis.set(product.id.toString(), sesami as unknown as SesamiSDKGlobal);
      });

      console.log('Updated product sesamis', updatedProductSesamis);

      setProductSesamis(updatedProductSesamis);
    }
  }, [config.products]);

  useEffect(() => {
    if (!selectedDay) {
      setSelectedDayAvailabilities([]);
      return;
    }

    const availabilities = dayAvailabilities.get(selectedProduct)?.get(selectedDay.toDateString());

    setSelectedDayAvailabilities(availabilities || []);
  }, [selectedDay, selectedProduct]);

  const handleClose = useCallback(() => {
    const placeholder = document.querySelector('.medispa-calendar--placeholder');

    setSelectedDay(null);
    setSelectedTimeSlot(null);

    placeholder!.classList.remove('active');
    document.documentElement.classList.remove('lock-scrolling');
  }, []);

  const handleCloseWithPrefillCapture = useCallback(() => {
    if (selectedTimeSlot) {
      const [year, month, day] = selectedTimeSlot.startTime.split(' ')[0].split('-');
      const product = config.products.find((p) => p.id.toString() === selectedProduct) ?? null;
      appointmentPrefillRef.current = {
        date: `${day}/${month}/${year}`,
        time: selectedTimeSlot.startTime.split(' ')[1],
        product,
      };
    }
    handleClose();
  }, [selectedTimeSlot, selectedProduct, config.products, handleClose]);

  return (
    <>
      <div className="medispa-calendar_backdrop" onClick={handleClose}></div>
      <div className="medispa-calendar_popup">
        <button type="button" className="medispa-calendar_close" onClick={handleClose}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M33.6464 15.8678C33.8417 15.6726 33.8417 15.356 33.6464 15.1607L32.3393 13.8536C32.144 13.6583 31.8274 13.6583 31.6322 13.8536L24 21.4857L16.3678 13.8536C16.1726 13.6583 15.856 13.6583 15.6607 13.8536L14.3536 15.1607C14.1583 15.356 14.1583 15.6726 14.3536 15.8678L21.9857 23.5L14.3536 31.1322C14.1583 31.3274 14.1583 31.644 14.3536 31.8393L15.6607 33.1464C15.856 33.3417 16.1726 33.3417 16.3678 33.1464L24 25.5143L31.6322 33.1464C31.8274 33.3417 32.144 33.3417 32.3393 33.1464L33.6464 31.8393C33.8417 31.644 33.8417 31.3274 33.6464 31.1322L26.0143 23.5L33.6464 15.8678Z"
              fill="currentColor"
            ></path>
          </svg>
        </button>

        <div className="medispa-calendar_popup-header">
          <h3 className="medispa-calendar_title">{productName}</h3>
        </div>

        <div className="medispa-calendar_tabs">
          {config.products.map((product) => (
            <ClinicTab
              key={product.id}
              clinicTitle={getCleanClinicName({
                clinics: config.clinics,
                handle: product.handle,
                defaultName: product.name,
                template: config.labels.clinic.tabLabel,
              })}
              clinicId={product.id.toString()}
              clinicHandle={product.handle}
              defaultActive={product.id.toString() === selectedProduct}
              setActiveClinic={setselectedProduct}
            />
          ))}
        </div>

        {config.products.map((product) => {
          let matchingClinicHandle = Object.keys(config.clinics).find(
            (key) => product.handle.indexOf(key) !== -1,
          );

          if (!matchingClinicHandle && product.handle.includes('-discovery-bar')) {
            matchingClinicHandle = Object.keys(config.clinics).find((key) =>
              key.includes('central'),
            );
          }

          if (!matchingClinicHandle) {
            return null;
          }

          return (
            <ClinicDetails
              key={product.id}
              clinicId={product.id.toString()}
              clinicDetails={config.clinics[matchingClinicHandle]}
              active={product.id.toString() === selectedProduct}
            />
          );
        })}

        {/* <h4 className="medispa-calendar_treatment-title">{productName}</h4> */}

        <span className="medispa-calendar_disclaimer">{config.labels.disclaimer}</span>

        {!selectedDay && <CalendarHead />}
        {!selectedDay && <CalendarDayTitles />}

        <div className="medispa-calendar--interactive-area">
          {!selectedDay && <CalendarGrid />}

          {selectedDayAvailabilities.length > 0 && selectedDay && (
            <CalendarTimeSlots day={selectedDay} slots={selectedDayAvailabilities} />
          )}

          {selectedDay && selectedTimeSlot && (
            <CalendarConfirm
              depositPrice={formatMoney(depositFullAmount, '${{amount_no_decimals}}')}
              storePrice={formatMoney(inStoreFullAmount, '${{amount_no_decimals}}')}
              reserveSlot={reserveSlot}
              onClose={handleCloseWithPrefillCapture}
            />
          )}

          {loading && (
            <div className="medispa-calendar_loader">
              <ScaleLoader color="#333" />
            </div>
          )}
        </div>
      </div>

      {errorMessage.length > 0 && (
        <ErrorPopup
          title={config.labels.error_popup_title}
          message={errorMessage}
          closeLabel={config.labels.error_popup_close_label}
          onClose={() => {
            setErrorMessage('');
            handleClose();
          }}
        />
      )}
    </>
  );
}
